import { useEffect, useState } from 'react';
import axios from 'axios';
import { Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Download delle immagini VM del broker dentro la guida "Installare il broker dall'immagine"
// (26/10/2026): la guida diceva solo di andare in Sites & Brokers. Stesse API del riquadro
// "Broker download" di pages/TenantDetailPage.tsx: link firmato valido 10 minuti, admin e analyst.
interface ApplianceImage { name: string; size: number; modifiedAt: string; md5: string | null }

const APPLIANCE_LABELS: Record<string, string> = {
  ova: 'VMware', vma: 'Sangfor HCI', qcow2: 'KVM (Proxmox, NexaVM, oVirt)', conf: 'KVM VM config',
};
const label = (name: string) => APPLIANCE_LABELS[name.split('.').pop()!.toLowerCase()] || name;

export default function ApplianceDownloads() {
  const { canEdit } = useAuth();
  const [images, setImages] = useState<ApplianceImage[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canEdit) return;
    axios.get('/api/brokers/appliances').then(res => setImages(res.data.data)).catch(() => setImages([]));
  }, [canEdit]);

  async function download(name: string) {
    setError(null);
    try {
      const res = await axios.post(`/api/brokers/appliances/${encodeURIComponent(name)}/link`);
      window.location.href = res.data.data.url;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not prepare the image download');
    }
  }

  return (
    <div className="mb-4 rounded-lg border border-primary-200 bg-primary-50/60 px-4 py-3 max-w-3xl lg:ml-[232px] print:hidden">
      <p className="text-sm font-medium text-gray-900 mb-2">Download the VM image</p>
      {!canEdit ? (
        <p className="text-xs text-gray-600">Only admin and analyst users can download the images. Ask one of them in your organisation.</p>
      ) : images === null ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : images.length === 0 ? (
        <p className="text-xs text-gray-600">No image is available on the platform right now. Contact NextGen Distribution support.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {images.filter(i => !i.name.endsWith('.conf')).map(i => (
              <button key={i.name} onClick={() => download(i.name)} title={i.name}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium text-primary-700 bg-white border border-primary-200 hover:bg-primary-100">
                <Download size={14} /> {label(i.name)} · {(i.size / 1024 ** 3).toFixed(1)} GB
              </button>
            ))}
          </div>
          {images.some(i => i.md5) && (
            <table className="text-xs mt-2">
              <tbody>
                {images.filter(i => i.md5).map(i => (
                  <tr key={i.name}>
                    <td className="pr-3 py-0.5 text-gray-500">{label(i.name)}</td>
                    <td className="pr-2 py-0.5 text-gray-400">MD5</td>
                    <td className="py-0.5 font-mono text-gray-700 select-all">{i.md5}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
    </div>
  );
}
